import {
  CloseOutlined,
  DeleteOutlined,
  DownOutlined,
  FolderOpenOutlined,
  HolderOutlined,
  PlusOutlined,
  RobotOutlined,
  UpOutlined,
} from "@ant-design/icons";
import { AdminFileManager, buildMediaFullUrl, StickyFormActions } from "@components";
import {
  Api,
  CHAT_LESSON_TOPIC_OPTIONS,
  canManageContentActivation,
  hasPermission,
  IWordCard,
  PERMISSIONS,
} from "@model";
import {
  Button,
  Card,
  Checkbox,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Tabs,
  Tooltip,
  message,
} from "antd";
import type { FormListFieldData } from "antd/es/form";
import {
  PointerEvent as ReactPointerEvent,
  useEffect,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import {
  WORD_CARD_LANGUAGES,
  WORD_CARD_LEVELS,
  cleanWordCardForSave,
  parseWordCardJson,
} from "./wordCardLanguages";

const toStoredMediaPath = (fullUrl: string): string => {
  try {
    return new URL(fullUrl).pathname.replace(/^\//, "");
  } catch {
    return fullUrl.replace(/^https?:\/\/[^/]+\//, "").replace(/^\//, "");
  }
};

const mediaPreviewSrc = (path?: string) => {
  if (!path) return "";
  if (path.startsWith("http")) return path;
  return buildMediaFullUrl(path);
};

function clampPanelPosition(
  left: number,
  top: number,
  width: number,
  height: number,
): { left: number; top: number } {
  const margin = 8;
  const maxLeft = Math.max(margin, window.innerWidth - width - margin);
  const maxTop = Math.max(margin, window.innerHeight - height - margin);
  return {
    left: Math.min(Math.max(margin, left), maxLeft),
    top: Math.min(Math.max(margin, top), maxTop),
  };
}

const StringList = ({
  name,
  label,
  placeholder,
}: {
  name: (string | number)[];
  label: string;
  placeholder: string;
}) => (
  <Form.Item label={label} style={{ marginBottom: 8 }}>
    <Form.List name={name}>
      {(fields, { add, remove }) => (
        <>
          {fields.map((field) => (
            <Space key={field.key} style={{ display: "flex", marginBottom: 8 }} align="start">
              <Form.Item name={field.name} style={{ flex: 1, marginBottom: 0 }}>
                <Input placeholder={placeholder} />
              </Form.Item>
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={() => remove(field.name)}
              />
            </Space>
          ))}
          <Button type="dashed" onClick={() => add("")} icon={<PlusOutlined />} block>
            Add {label.toLowerCase()}
          </Button>
        </>
      )}
    </Form.List>
  </Form.Item>
);

const SampleList = ({ name }: { name: (string | number)[] }) => (
  <Form.Item label="Samples" style={{ marginBottom: 0 }}>
    <Form.List name={name}>
      {(fields, { add, remove }) => (
        <>
          {fields.map((field) => (
            <Space
              key={field.key}
              style={{ display: "flex", marginBottom: 8 }}
              align="start"
            >
              <Form.Item
                name={[field.name, "text"]}
                style={{ flex: 2, marginBottom: 0 }}
              >
                <Input placeholder="Sample sentence" />
              </Form.Item>
              <Form.Item
                name={[field.name, "highlight"]}
                style={{ flex: 1, marginBottom: 0 }}
              >
                <Input placeholder="Highlight" />
              </Form.Item>
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={() => remove(field.name)}
              />
            </Space>
          ))}
          <Button
            type="dashed"
            onClick={() => add({ text: "", highlight: "" })}
            icon={<PlusOutlined />}
            block
          >
            Add sample
          </Button>
        </>
      )}
    </Form.List>
  </Form.Item>
);

const WordLanguageFields = ({
  field,
  langKey,
}: {
  field: FormListFieldData;
  langKey: string;
}) => (
  <>
    <Form.Item name={[field.name, langKey, "word"]} label="Word">
      <Input placeholder="Word" />
    </Form.Item>
    <Form.Item name={[field.name, langKey, "partOfSpeech"]} label="Part of speech">
      <Input placeholder="interjection" />
    </Form.Item>
    <StringList
      name={[field.name, langKey, "more"]}
      label="More"
      placeholder="A short meaning or note"
    />
    <SampleList name={[field.name, langKey, "samples"]} />
  </>
);

const WordCardCreatePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [form] = Form.useForm<IWordCard>();
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!!id);
  const [photoModalOpen, setPhotoModalOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importJsonText, setImportJsonText] = useState("");
  const [aiPanelOpen, setAiPanelOpen] = useState(false);
  const [aiPanelExpanded, setAiPanelExpanded] = useState(true);
  const [aiDescription, setAiDescription] = useState("");
  const [descriptionTouched, setDescriptionTouched] = useState(false);
  const [aiDescriptionRtl, setAiDescriptionRtl] = useState(false);
  const [aiPanelPos, setAiPanelPos] = useState<{ left: number; top: number } | null>(
    null,
  );
  const [aiPanelDragging, setAiPanelDragging] = useState(false);
  const aiPanelRef = useRef<HTMLDivElement>(null);
  const aiDragRef = useRef<{
    startX: number;
    startY: number;
    originLeft: number;
    originTop: number;
    moved: boolean;
  } | null>(null);
  const photo = Form.useWatch("photo", form) as string | undefined;
  const isCreateMode = !id;
  const canSave = isCreateMode
    ? hasPermission(PERMISSIONS.wordCardsCreate)
    : hasPermission(PERMISSIONS.wordCardsEdit);
  const canActivate = canManageContentActivation();
  const returnTo =
    (location.state as { returnTo?: string } | null)?.returnTo ??
    "/panel/word-cards";

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    Api.get(`/api/admin/panel/word-cards/${id}`)
      .then((response) => {
        form.setFieldsValue(response.data.data);
      })
      .catch(() => message.error("Failed to load word card"))
      .finally(() => setLoading(false));
  }, [id, form]);

  const openAiPanel = () => {
    const saved = form.getFieldValue("aiGenerateDescriptions") as
      | string[]
      | undefined;
    setAiDescription((saved ?? []).map((line) => String(line ?? "").trim()).filter(Boolean).join("\n"));
    setAiPanelOpen(true);
    setAiPanelExpanded(true);
  };

  const syncDescription = (value: string) => {
    setDescriptionTouched(true);
    setAiDescription(value);
    const trimmed = value.trim();
    form.setFieldValue("aiGenerateDescriptions", trimmed ? [trimmed] : []);
  };

  const handleAiGenerate = async () => {
    const description = aiDescription.trim();
    if (!description) {
      message.error("Write what this card should teach");
      return;
    }
    const descriptions = [description];
    const values = form.getFieldsValue(true) as IWordCard;
    const wordCard = {
      title: values.title,
      metadata: values.metadata,
      topic: values.topic,
      category: values.category,
      photo: values.photo,
      avatarPrompt: values.avatarPrompt,
      words: values.words,
      isActive: values.isActive,
    };
    setGenerating(true);
    try {
      const response = await Api.post("/api/admin/panel/word-cards/generate", {
        descriptions,
        wordCard,
      });
      const generated = response.data?.data as IWordCard | undefined;
      const generatedCount = WORD_CARD_LEVELS.reduce(
        (sum, level) => sum + (generated?.words?.[level]?.length ?? 0),
        0,
      );
      if (!generatedCount) {
        message.error("AI returned no words");
        return;
      }
      form.setFieldsValue({
        ...generated,
        photo: values.photo,
        isActive: values.isActive,
        aiGenerateDescriptions: descriptions,
      });
      message.success("Word card generated — review and save when ready");
    } catch {
      message.error("Failed to generate word card");
    } finally {
      setGenerating(false);
    }
  };

  const onAiHeaderPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if ((event.target as HTMLElement).closest("button")) return;
    const panel = aiPanelRef.current;
    if (!panel) return;
    const rect = panel.getBoundingClientRect();
    const originLeft = aiPanelPos?.left ?? rect.left;
    const originTop = aiPanelPos?.top ?? rect.top;
    if (!aiPanelPos) setAiPanelPos({ left: rect.left, top: rect.top });
    aiDragRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      originLeft,
      originTop,
      moved: false,
    };
    setAiPanelDragging(true);
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const onAiHeaderPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const drag = aiDragRef.current;
    const panel = aiPanelRef.current;
    if (!drag || !panel) return;
    const dx = event.clientX - drag.startX;
    const dy = event.clientY - drag.startY;
    if (!drag.moved && Math.hypot(dx, dy) < 4) return;
    drag.moved = true;
    const { width, height } = panel.getBoundingClientRect();
    setAiPanelPos(
      clampPanelPosition(drag.originLeft + dx, drag.originTop + dy, width, height),
    );
  };

  const onAiHeaderPointerUp = (event: ReactPointerEvent<HTMLDivElement>) => {
    const drag = aiDragRef.current;
    aiDragRef.current = null;
    setAiPanelDragging(false);
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    if (!drag?.moved && !aiPanelExpanded) setAiPanelExpanded(true);
  };

  const handleImportJson = () => {
    let imported: IWordCard;
    try {
      imported = parseWordCardJson(importJsonText);
    } catch (error) {
      message.error(
        error instanceof Error && error.message.startsWith("JSON must")
          ? error.message
          : "Invalid JSON",
      );
      return;
    }

    form.resetFields();
    form.setFieldsValue(imported);
    setAiDescription((imported.aiGenerateDescriptions ?? []).join("\n"));
    setDescriptionTouched(false);
    setImportModalOpen(false);
    setImportJsonText("");
    message.success("Word card imported. Review it, then save.");
  };

  const onFinish = async (values: IWordCard) => {
    const stored = form.getFieldsValue(true) as IWordCard;
    const payload = cleanWordCardForSave({
      ...values,
      aiGenerateDescriptions: descriptionTouched
        ? aiDescription.trim()
          ? [aiDescription.trim()]
          : []
        : stored.aiGenerateDescriptions,
    });
    const wordCount = WORD_CARD_LEVELS.reduce(
      (sum, level) => sum + (payload.words?.[level]?.length ?? 0),
      0,
    );
    if (!wordCount) {
      message.error("Add at least one word");
      return;
    }
    setSaving(true);
    try {
      if (isCreateMode) {
        await Api.post("/api/admin/panel/word-cards", payload);
        message.success("Word card created");
      } else {
        await Api.put(`/api/admin/panel/word-cards/${id}`, payload);
        message.success("Word card saved");
      }
      navigate(returnTo);
    } catch {
      message.error("Failed to save word card");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card loading={loading}>
      <Form
        form={form}
        layout="vertical"
        initialValues={{ isActive: false, level: "A1", words: [], category: [] }}
        onFinish={onFinish}
      >
        <StickyFormActions
          onBack={() => navigate(returnTo)}
          backLabel="Back"
          actions={
            canSave ? (
              <Space>
                <Tooltip title="Describe the card. The current form is sent with the description list.">
                  <Button
                    size="large"
                    icon={<RobotOutlined />}
                    onClick={openAiPanel}
                    style={{
                      background:
                        "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      borderColor: "transparent",
                      color: "#fff",
                    }}
                  >
                    AI Generate
                  </Button>
                </Tooltip>
                <Button size="large" onClick={() => setImportModalOpen(true)}>
                  Import from JSON
                </Button>
                <Button loading={saving} htmlType="submit" type="primary" size="large">
                  {isCreateMode ? "Create" : "Save"}
                </Button>
              </Space>
            ) : null
          }
        />

        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr auto", gap: 16 }}>
          <Form.Item
            name="title"
            label="Title"
            rules={[{ required: true, message: "Required" }]}
          >
            <Input placeholder="Daily Life" />
          </Form.Item>
          <Form.Item
            name="topic"
            label="Topic"
            rules={[{ required: true, message: "Please select a topic" }]}
          >
            <Select
              placeholder="Select topic"
              options={CHAT_LESSON_TOPIC_OPTIONS}
              showSearch
              optionFilterProp="label"
            />
          </Form.Item>
          <Form.Item name="isActive" valuePropName="checked" label=" ">
            <Checkbox disabled={!canActivate}>Active</Checkbox>
          </Form.Item>
        </div>

        <Form.Item name="category" label="Category">
          <Select
            mode="tags"
            open={false}
            suffixIcon={null}
            tokenSeparators={[","]}
            style={{ width: "100%" }}
            placeholder="Type a category and press Enter"
          />
        </Form.Item>

        <Form.Item label="Photo">
          <Space direction="vertical" size={8} style={{ width: "100%" }}>
            <div style={{ display: "flex", gap: 8, alignItems: "flex-start", width: "100%" }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <Form.Item name="avatarPrompt" noStyle>
                  <Input.TextArea
                    rows={3}
                    placeholder="Image prompt. Paste this into GPT to generate the card photo."
                  />
                </Form.Item>
              </div>
              <Button icon={<FolderOpenOutlined />} onClick={() => setPhotoModalOpen(true)}>
                File manager
              </Button>
            </div>
            <Form.Item name="photo" noStyle>
              <Input placeholder="images/..." />
            </Form.Item>
            {photo && (
              <img
                src={mediaPreviewSrc(photo)}
                alt=""
                style={{ width: 72, height: 72, objectFit: "cover", borderRadius: 8 }}
              />
            )}
          </Space>
        </Form.Item>

        <Form.Item label="Card text">
          <Tabs
            items={WORD_CARD_LANGUAGES.map((lang) => ({
              key: lang.key,
              label: lang.label,
              forceRender: true,
              children: (
                <>
                  <Form.Item name={["metadata", lang.key, "title"]} label="Title">
                    <Input placeholder={`Title in ${lang.label}`} />
                  </Form.Item>
                  <Form.Item
                    name={["metadata", lang.key, "description"]}
                    label="Description"
                  >
                    <Input.TextArea rows={2} placeholder={`Description in ${lang.label}`} />
                  </Form.Item>
                </>
              ),
            }))}
          />
        </Form.Item>

        <Tabs
          items={WORD_CARD_LEVELS.map((level) => ({
            key: level,
            label: level,
            forceRender: true,
            children: (
              <Form.List name={["words", level]}>
                {(fields, { add, remove }) => (
                  <>
                    {fields.map((field, index) => (
                      <Card
                        key={field.key}
                        size="small"
                        title={`${level} word ${index + 1}`}
                        style={{ marginBottom: 16 }}
                        extra={
                          <Button
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => remove(field.name)}
                          >
                            Remove
                          </Button>
                        }
                      >
                        <Tabs
                          items={WORD_CARD_LANGUAGES.map((lang) => ({
                            key: lang.key,
                            label: lang.label,
                            forceRender: true,
                            children: (
                              <WordLanguageFields field={field} langKey={lang.key} />
                            ),
                          }))}
                        />
                      </Card>
                    ))}
                    <Button
                      type="dashed"
                      icon={<PlusOutlined />}
                      onClick={() => add({ en: { word: "" } })}
                      block
                    >
                      Add {level} word
                    </Button>
                  </>
                )}
              </Form.List>
            ),
          }))}
        />
      </Form>

      <Modal
        title="Import word card from JSON"
        open={importModalOpen}
        onCancel={() => setImportModalOpen(false)}
        onOk={handleImportJson}
        okText="Import"
        okButtonProps={{ disabled: !importJsonText.trim() }}
        width={720}
        destroyOnClose
      >
        <p style={{ color: "#8c8c8c" }}>
          Paste a word card document copied from mongo. This replaces every field
          in the form; nothing is saved until you press{" "}
          {isCreateMode ? "Create" : "Save"}.
        </p>
        <Input.TextArea
          value={importJsonText}
          onChange={(event) => setImportJsonText(event.target.value)}
          rows={16}
          spellCheck={false}
          style={{ fontFamily: "monospace" }}
          placeholder='{ "title": "Shopping", "topic": "daily_conversation", "words": { "A1": [], "B1": [], "C1": [] } }'
        />
      </Modal>

      <Modal
        title="File manager"
        open={photoModalOpen}
        onCancel={() => setPhotoModalOpen(false)}
        footer={null}
        width="min(1320px, 96vw)"
        style={{ top: 24 }}
        bodyStyle={{ maxHeight: "calc(100vh - 180px)", overflow: "auto" }}
        destroyOnClose
      >
        <AdminFileManager
          defaultFolder="images"
          onSelectUrl={(fullUrl) => {
            form.setFieldValue("photo", toStoredMediaPath(fullUrl));
            setPhotoModalOpen(false);
          }}
        />
      </Modal>

      {aiPanelOpen && (
        <div
          ref={aiPanelRef}
          style={{
            position: "fixed",
            width: "min(720px, calc(100vw - 32px))",
            zIndex: 1001,
            borderRadius: aiPanelPos ? 12 : "12px 12px 0 0",
            border: "1px solid #d9d9d9",
            borderBottom: aiPanelPos ? "1px solid #d9d9d9" : "none",
            background: "#fff",
            boxShadow: aiPanelPos
              ? "0 12px 32px rgba(0, 0, 0, 0.16)"
              : "0 -8px 28px rgba(0, 0, 0, 0.12)",
            overflow: "hidden",
            touchAction: "none",
            ...(aiPanelPos
              ? { left: aiPanelPos.left, top: aiPanelPos.top }
              : {
                  left: "50%",
                  bottom: 0,
                  transform: "translateX(-50%)",
                }),
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "10px 14px",
              borderBottom: aiPanelExpanded ? "1px solid #f0f0f0" : undefined,
              background:
                "linear-gradient(135deg, rgba(102, 126, 234, 0.08) 0%, rgba(118, 75, 162, 0.08) 100%)",
              cursor: aiPanelDragging ? "grabbing" : "grab",
              userSelect: "none",
            }}
            onPointerDown={onAiHeaderPointerDown}
            onPointerMove={onAiHeaderPointerMove}
            onPointerUp={onAiHeaderPointerUp}
            onPointerCancel={onAiHeaderPointerUp}
          >
            <HolderOutlined style={{ color: "rgba(0,0,0,0.35)", fontSize: 14 }} />
            <RobotOutlined style={{ color: "#667eea", fontSize: 16 }} />
            <span style={{ flex: 1, fontWeight: 600, fontSize: 14 }}>
              AI word card generator
            </span>
            {generating && (
              <span style={{ fontSize: 12, color: "rgba(0,0,0,0.45)" }}>
                Generating…
              </span>
            )}
            <Tooltip title={aiPanelExpanded ? "Collapse" : "Expand"}>
              <Button
                type="text"
                size="small"
                aria-label={aiPanelExpanded ? "Collapse panel" : "Expand panel"}
                icon={aiPanelExpanded ? <DownOutlined /> : <UpOutlined />}
                onClick={(event) => {
                  event.stopPropagation();
                  setAiPanelExpanded((open) => !open);
                }}
              />
            </Tooltip>
            <Tooltip title="Close">
              <Button
                type="text"
                size="small"
                aria-label="Close AI generator panel"
                icon={<CloseOutlined />}
                onClick={(event) => {
                  event.stopPropagation();
                  setAiPanelOpen(false);
                }}
              />
            </Tooltip>
          </div>
          {aiPanelExpanded && (
            <div style={{ maxHeight: "min(70vh, 560px)", overflow: "auto", padding: 16 }}>
              <Space direction="vertical" size={12} style={{ width: "100%" }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div style={{ fontSize: 13, fontWeight: 500 }}>
                    What should this card teach?
                  </div>
                  <Checkbox
                    checked={aiDescriptionRtl}
                    onChange={(event) => setAiDescriptionRtl(event.target.checked)}
                  >
                    RTL
                  </Checkbox>
                </div>
                <Input.TextArea
                  value={aiDescription}
                  autoSize={{ minRows: 4, maxRows: 10 }}
                  dir={aiDescriptionRtl ? "rtl" : "ltr"}
                  style={{ textAlign: aiDescriptionRtl ? "right" : "left" }}
                  placeholder="e.g. Greetings used when meeting someone"
                  onChange={(event) => syncDescription(event.target.value)}
                />
                <Button
                  type="primary"
                  icon={<RobotOutlined />}
                  loading={generating}
                  onClick={handleAiGenerate}
                  block
                  style={{
                    background: generating
                      ? undefined
                      : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    borderColor: "transparent",
                  }}
                >
                  {generating ? "Generating word card…" : "Generate word card"}
                </Button>
              </Space>
            </div>
          )}
        </div>
      )}
    </Card>
  );
};

export default WordCardCreatePage;
